import { motion } from "framer-motion";
import { ArrowLeft, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { WizardState } from "./index";

interface TimeStepProps {
  onNext: () => void;
  onBack: () => void;
  updateAnswer: (key: keyof WizardState, value: string | number) => void;
  updateScore: (points: number) => void;
  currentAnswer: number;
}

const TimeStep = ({
  onNext,
  onBack,
  updateAnswer,
  updateScore,
  currentAnswer,
}: TimeStepProps) => {
  const handleChange = (value: number[]) => {
    const newValue = value[0];
    const previousValue = currentAnswer;

    // Update score logic: +10 if > 10 hours
    if (previousValue <= 10 && newValue > 10) {
      updateScore(10);
    } else if (previousValue > 10 && newValue <= 10) {
      updateScore(-10);
    }

    updateAnswer("hoursPerWeek", newValue);
  };

  const handleNext = () => {
    if (currentAnswer > 0) {
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
          How many hours per week do you spend on this?
        </h2>
        <p className="text-muted-foreground">Move the slider to indicate your time investment</p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="p-8 sm:p-12 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
      >
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gold/20 to-gold-light/20 flex items-center justify-center">
            <Clock className="w-8 h-8 text-gold" />
          </div>
        </div>

        <div className="text-center mb-8">
          <div className="text-6xl font-bold text-foreground mb-2">
            {currentAnswer}
            <span className="text-3xl text-muted-foreground ml-2">hrs/week</span>
          </div>
          {currentAnswer > 10 && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-gold text-sm font-medium"
            >
              Significant automation opportunity
            </motion.p>
          )}
        </div>

        <Slider
          value={[currentAnswer]}
          onValueChange={handleChange}
          max={40}
          step={1}
          className="w-full"
        />

        <div className="flex justify-between text-xs text-muted-foreground mt-2">
          <span>0 hrs</span>
          <span>40+ hrs</span>
        </div>
      </motion.div>

      <div className="flex justify-between items-center pt-4">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-muted-foreground hover:text-foreground hover:bg-white/5"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Button
          onClick={handleNext}
          disabled={currentAnswer === 0}
          className="bg-gradient-to-r from-gold via-gold-light to-gold hover:shadow-[0_0_30px_hsl(var(--gold)/0.45)] text-gold-foreground font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default TimeStep;
